// Builds the side menu tree from the flat menu rows of the database
// (initData.menus). The database stays the source of truth for what each
// user can open; this only arranges it:
//  - children are grouped under their module (accordion);
//  - the module entries themselves (/solicitudes-compra, /ordenes-compra,
//    /presupuestos, /finanzas, /contabilidad) open the dashboards, so they
//    are listed together under "Reportes";
//  - duplicated rows are dropped and children whose parent row is missing
//    still get their module group.

export interface MenuRow {
  id?: string | number;
  um_id: string | number;
  um_idPadre?: string | number | null;
  um_codigo?: string;
  um_nombre: string;
  um_url_path: string;
  um_icon: string;
}

export interface MenuLink {
  key: string;
  codigo: string;
  nombre: string;
  url: string;
  // PrimeIcons class, or null to use the database FontAwesome icon
  icono: string | null;
  iconoBd: string;
}

export interface MenuGroup {
  key: string;
  nombre: string;
  icono: string | null;
  iconoBd: string;
  links: MenuLink[];
}

export interface MenuTree {
  inicio: MenuLink | null;
  // Plain top level links without children (none today)
  sueltos: MenuLink[];
  grupos: MenuGroup[];
  reportes: MenuGroup | null;
  // "SALIR" row present: the user has the logout entry
  tieneSalir: boolean;
}

// Module rows (no parent) whose link opens a dashboard
interface Modulo {
  nombre: string;
  icono: string;
  reporte: string;
  iconoReporte: string;
}

const MODULOS: Record<string, Modulo> = {
  INISOC: {
    nombre: "Solicitudes de compra",
    icono: "pi pi-file-edit",
    reporte: "Solicitudes de compra",
    iconoReporte: "pi pi-chart-bar",
  },
  INIOC: {
    nombre: "Órdenes de compra",
    icono: "pi pi-shopping-cart",
    reporte: "Órdenes de compra",
    iconoReporte: "pi pi-chart-line",
  },
  INIPRES: {
    nombre: "Presupuestos",
    icono: "pi pi-wallet",
    reporte: "Presupuestos",
    iconoReporte: "pi pi-chart-pie",
  },
  FININI: {
    nombre: "Finanzas",
    icono: "pi pi-dollar",
    reporte: "Finanzas",
    iconoReporte: "pi pi-money-bill",
  },
  CONTINI: {
    nombre: "Contabilidad",
    icono: "pi pi-calculator",
    reporte: "Contabilidad",
    iconoReporte: "pi pi-book",
  },
  // Report-only row (no children): seguimiento de correos de OC emitidas
  ININOTIF: {
    nombre: "Notificaciones OC",
    icono: "pi pi-envelope",
    reporte: "Notificaciones OC",
    iconoReporte: "pi pi-envelope",
  },
};

// Module of a child whose parent row did not come (parent id -> code)
const MODULO_POR_ID_PADRE: Record<string, string> = {
  "2": "INISOC",
  "7": "INIOC",
  "12": "INIPRES",
  "15": "FININI",
  "17": "CONTINI",
};

const ICONOS: Record<string, string> = {
  INI: "pi pi-home",
  SOCNVA: "pi pi-plus-circle",
  SOCMAN: "pi pi-search",
  SOCAPR: "pi pi-check-square",
  SOCCONF: "pi pi-verified",
  OCNVA: "pi pi-plus-circle",
  OCMAN: "pi pi-search",
  OCAPR: "pi pi-check-square",
  OCCONF: "pi pi-verified",
  VISGGMON: "pi pi-exclamation-circle",
  PRESNVO: "pi pi-plus-circle",
  MANFIN: "pi pi-list-check",
  MANCONT: "pi pi-list-check",
};

const codigoDe = (r: MenuRow) => (r.um_codigo ?? "").trim().toUpperCase();
const idDe = (v: unknown) => (v === null || v === undefined ? "" : String(v));

const aLink = (r: MenuRow): MenuLink => {
  const codigo = codigoDe(r);
  return {
    key: `m-${idDe(r.um_id)}`,
    codigo,
    nombre: (r.um_nombre ?? "").trim(),
    url: r.um_url_path,
    icono: ICONOS[codigo] ?? null,
    iconoBd: r.um_icon,
  };
};

/** Path part of a menu url (some carry a query string) */
export const rutaDe = (url: string) => (url ?? "").split("?")[0];

export function construirMenu(rows: MenuRow[] | null | undefined): MenuTree {
  const vistos = new Set<string>();
  const filas = (rows ?? []).filter((r) => {
    const id = idDe(r?.um_id);
    if (!r || !id || vistos.has(id)) return false;
    vistos.add(id);
    return true;
  });

  const tieneSalir = filas.some((r) => codigoDe(r) === "SALIR");
  const utiles = filas.filter((r) => codigoDe(r) !== "SALIR");
  const raices = utiles.filter((r) => !idDe(r.um_idPadre));
  const hijos = utiles.filter((r) => idDe(r.um_idPadre));

  let inicio: MenuLink | null = null;
  const sueltos: MenuLink[] = [];
  const reportes: MenuLink[] = [];
  // Insertion order = order of the database rows
  const grupos = new Map<string, MenuGroup>();

  const grupo = (idPadre: string, fila?: MenuRow): MenuGroup => {
    const existente = grupos.get(idPadre);
    if (existente) return existente;
    const codigo = fila ? codigoDe(fila) : (MODULO_POR_ID_PADRE[idPadre] ?? "");
    const modulo = MODULOS[codigo];
    const nuevo: MenuGroup = {
      key: `g-${idPadre}`,
      nombre: modulo?.nombre ?? fila?.um_nombre?.trim() ?? "Otros",
      icono: modulo?.icono ?? (fila ? null : "pi pi-folder"),
      iconoBd: fila?.um_icon ?? "faFolder",
      links: [],
    };
    grupos.set(idPadre, nuevo);
    return nuevo;
  };

  // Walk the rows once, in database order, so groups keep that order
  utiles.forEach((r) => {
    const id = idDe(r.um_id);
    const codigo = codigoDe(r);
    const idPadre = idDe(r.um_idPadre);

    if (!idPadre) {
      if (codigo === "INI") {
        inicio = aLink(r);
        return;
      }
      const modulo = MODULOS[codigo];
      if (modulo) {
        // Its own link is the module dashboard: listed under Reportes
        reportes.push({
          ...aLink(r),
          nombre: modulo.reporte,
          icono: modulo.iconoReporte,
        });
        if (hijos.some((h) => idDe(h.um_idPadre) === id)) grupo(id, r);
        return;
      }
      if (hijos.some((h) => idDe(h.um_idPadre) === id)) {
        // Unknown module with children: keep its own page as first entry
        grupo(id, r).links.unshift({
          ...aLink(r),
          nombre: "Inicio del módulo",
        });
      } else {
        sueltos.push(aLink(r));
      }
      return;
    }

    grupo(idPadre).links.push(aLink(r));
  });

  // Groups created from a child before their parent row: take the parent's
  // name / icon when it exists
  raices.forEach((r) => {
    const g = grupos.get(idDe(r.um_id));
    if (!g) return;
    const modulo = MODULOS[codigoDe(r)];
    g.nombre = modulo?.nombre ?? r.um_nombre?.trim() ?? g.nombre;
    g.icono = modulo?.icono ?? null;
    g.iconoBd = r.um_icon ?? g.iconoBd;
  });

  return {
    inicio,
    sueltos,
    grupos: Array.from(grupos.values()).filter((g) => g.links.length),
    reportes: reportes.length
      ? {
          key: "g-reportes",
          nombre: "Reportes",
          icono: "pi pi-chart-bar",
          iconoBd: "faChartPie",
          links: reportes,
        }
      : null,
    tieneSalir,
  };
}
